import {
    CabinetDocument,
    CabinetLetter,
    CabinetTranscription,
    FileCabinetCapabilities,
    FileCabinetDataSource,
    fileCabinetCarriedDocumentId,
    fileCabinetDemoFilter,
    fileCabinetDemoPage,
    fileCabinetDemoSort,
    FileCabinetFileUrls,
    FileCabinetListKey,
    FileCabinetListRequest,
    FileCabinetPage,
    FileCabinetRecord,
    FileCabinetRecordByList,
    FileCabinetRecordByView,
    fileCabinetRecordId,
    FileCabinetViewKey,
    fileCabinetWireFilters,
    ProviderDisplayDocumentType,
} from '@enter/core';
import type { ClaimGenerator } from 'stores/claim.generator';
import type { DocumentGenerator } from 'stores/document.generator';

/**
 * The File Cabinet in demo mode: the generators the Documents, Mail and Fax pages already use in
 * demo, so the same invented records show in both places (NS-14, NS-15). No request reaches the API.
 *
 * - Documents, received mail, letters, received and sent faxes: generated. All Documents also lists
 *   the documents the generated mail, letters and faxes carry, so "Attached to", "Referenced by" and
 *   "open the document" connect (NS-15). Each claim document has exactly one carrier — a scanned
 *   piece of mail (typed Mail), a received fax (typed Fax), a letter or a sent fax.
 * - Sent email, calls and AI calls: the demo has no generator for them, so the lists are empty —
 *   honestly empty, not invented here (NS-4).
 * - Filters: applied by `fileCabinetDemoFilter` where their meaning is plain. Full-text search, dates
 *   and numbers are not offered (`capabilities`); asked anyway, they fail as not available in demo,
 *   never with the unfiltered list.
 * - Transcripts: none.
 */
export class ProviderDemoFileCabinetDataSource implements FileCabinetDataSource {
    public readonly app = 'provider' as const;
    public readonly documentTypes: string[] = Object.values(ProviderDisplayDocumentType);
    public readonly capabilities: FileCabinetCapabilities = { fullTextSearch: false, rangeFilters: false, inactiveDocuments: false };

    constructor(
        private readonly claims: ClaimGenerator,
        private readonly documents: DocumentGenerator,
    ) {}

    public async list<L extends FileCabinetListKey>(
        list: L,
        request: FileCabinetListRequest,
    ): Promise<FileCabinetPage<FileCabinetRecordByList[L]>> {
        // The demo has no calls, of any method.
        if (list === 'call.all') {
            return { items: [], total: 0 };
        }
        const view = list as FileCabinetViewKey;
        // Everything, then filtered, sorted and paged here: the generators ignore filters, and their sort
        // reads the sort value as a field name, which misses (`RECEIVED` is `receivedDate`).
        const all = ((await this.listAll(view)) ?? []) as FileCabinetRecordByList[L][];
        const filtered = fileCabinetDemoFilter(view, all, fileCabinetWireFilters(view, request.filters));
        return fileCabinetDemoPage(fileCabinetDemoSort(filtered, request.sort, request.order), request.limit, request.offset);
    }

    public async get<V extends FileCabinetViewKey>(view: V, id: string): Promise<FileCabinetRecordByView[V] | undefined> {
        const all = (await this.listAll(view)) ?? [];
        return all.find(record => fileCabinetRecordId(view, record) === id) as FileCabinetRecordByView[V] | undefined;
    }

    public async fileUrls(view: FileCabinetViewKey, record: FileCabinetRecord): Promise<FileCabinetFileUrls | undefined> {
        // As live: a letter cancelled before it was printed has no file.
        if (view === 'mail.out' && (record as CabinetLetter).cancelled) {
            return undefined;
        }
        const documentId = view === 'docs' ? (record as CabinetDocument).id : fileCabinetCarriedDocumentId(view, record);
        if (!documentId) {
            return undefined;
        }
        const type =
            view === 'docs'
                ? (record as CabinetDocument).documentType
                : (record as { document?: CabinetDocument }).document?.documentType;
        const link = await this.claims.loadDocumentUrlByDocumentId(documentId, type as ProviderDisplayDocumentType | undefined);
        return link?.url ? { download: link.url, view: link.url } : undefined;
    }

    public async transcription(): Promise<CabinetTranscription | undefined> {
        return undefined;
    }

    public emptyNote(view: FileCabinetViewKey): string | undefined {
        return view === 'email.out' || view.startsWith('call.') ? 'Demo mode has no sample emails or calls.' : undefined;
    }

    private async listAll(view: FileCabinetViewKey): Promise<FileCabinetRecord[] | undefined> {
        const all = {} as never;
        switch (view) {
            case 'docs':
                return this.allDocuments();
            case 'mail.in':
                return (await this.claims.loadMailListing(all)).items;
            case 'mail.out':
                return (await this.claims.loadLetterListing(all)).items;
            case 'fax.in':
                return (await this.claims.loadReceiveFaxListing(all)).items;
            case 'fax.out':
                return (await this.claims.loadSendFaxListing(all)).items;
            default:
                return [];
        }
    }

    /** The document generator's documents and every document the generated mail, letters and faxes carry. */
    private async allDocuments(): Promise<CabinetDocument[]> {
        const all = {} as never;
        const [documents, mail, letters, received, sent] = await Promise.all([
            this.documents.loadDocumentListing(all),
            this.claims.loadMailListing(all),
            this.claims.loadLetterListing(all),
            this.claims.loadReceiveFaxListing(all),
            this.claims.loadSendFaxListing(all),
        ]);
        const byId = new Map<string, CabinetDocument>();
        for (const document of documents?.items ?? []) {
            if (document.id) {
                byId.set(document.id, document);
            }
        }
        for (const carrier of [...mail.items, ...letters.items, ...received.items, ...sent.items]) {
            const document = (carrier as { document?: CabinetDocument }).document;
            if (document?.id && !byId.has(document.id)) {
                byId.set(document.id, document);
            }
        }
        return [...byId.values()];
    }
}
